/**
 * This file holds actions. The actions commit mutations and can perform asynchronous operations.
 * Please refer to https://vuex.vuejs.org/guide/actions.html for more information
 */
import Vue from 'vue';
import axios from "axios";
import * as types from "./types";
import http from "@/http";
import returnUrlParams from "@/http/returnUrlParams";
import { ugcEntryPointData } from '@/data/modelsData';

const actions = {
  [types.UPLOAD](context, { inputFile }) {
    const data = new FormData();
    data.append("file-input", inputFile);

    const requestUrl = `${context.rootState.endPoints.ugcentrypoint.ugc}`;

    return new Promise((resolve, reject) => {
      http
        .post(requestUrl, data)
        .then(({ data }) => {
          if (this.state.UGCEntryPoint.enableEmailGateway) {
            context
              .dispatch(types.UGC_GATEWAY, {
                image_id: data.temp_image_id
              })
              .then(({ data }) => {
                resolve(data);
              })
              .catch(reject);
          }
          resolve(data);
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },
  /**
   * Upload image for checkerboarder tool
   */
  [types.UPLOAD_CHECKERED_IMAGE]({ dispatch, commit }, inputFile) {
    return new Promise((resolve, reject) => {
      dispatch(types.UPLOAD, { inputFile }).then(data => {
        commit(types.UPDATE_SECONDARY_IMAGE, {
          id: data.temp_image_id,
          url: data.ugc_image_path,
          type: data.ugc_image_path.split(".").pop(),
        });
        resolve(data);
      });
    });
  },
  /**
   * Get selectable Styles.
   * TODO update with API
   */
  [types.FETCH_PRODUCT_DATA]() {
    return Promise.resolve(ugcEntryPointData);
  },
  /**
   * @function GET_PDM
   * Gets the PDM for a given Model and commits miscellaneous configuration data to the global Vuex store.
   * @param {object} context The store
   * @param {function} context.commit
   * @param {object} config
   * @param {string} config.pdmModel Optional parameter, defaults to 'sk8-hi'
   */
  [types.GET_PDM]({ commit }, { pdmModel }) {
    const url = `${this.state.endPoints.ugcentrypoint.pdm}${pdmModel}.json`;

    return new Promise((resolve, reject) => http.get(url).then(({ data }) => {
      commit(types.SET_PDM, [pdmModel, data]);
      resolve(data);
    }).catch(reject));
  },
  /**
   * @function GET_SIZES
   * @param {*} context
   * @param {*} data
   */
  [types.GET_SIZES]({ commit }, { partNumber }) {
    const url = `${this.state.endPoints.ugcentrypoint.sizes}${partNumber}`;
    return new Promise((resolve, reject) => http.get(url).then(({ data }) => {
      commit(types.SET_SIZES, [partNumber, data]);
      resolve(data);
    }).catch(reject));
  },
  [types.UGC_GATEWAY](context, { image_id }) {
    const contactDetails = this.state.UGCEntryPoint.contactDetails;

    const requestUrl = `${this.state.endPoints.ugcentrypoint.ugcGateway}`;
    const optIn = this.state.UGCEntryPoint.optIn;
    const data = {
      "image_id": image_id,
      "first_name": contactDetails.firstName,
      "last_name": contactDetails.lastName,
      "email": contactDetails.emailAddress,
      "opt_in": optIn
    };
    return new Promise((resolve, reject) => {
      axios({
        url: requestUrl,
        method: "POST",
        headers: { "content-type": "application/json" },
        data: JSON.stringify(data)
      })
        .then(({ data }) => {
          resolve(data);
        })
        .catch(reject);
    });
  },
  [types.GET_UGC_PLACEMENT]({ rootState }, { model, size, componentName, placementName, imageBuffer }) {
    const requestUrl = `${this.state.endPoints.ugcentrypoint.ugcPlacementImage}`;


    const data = {
      componentName,
      productName: model,
      tonalstitch: '1',
      size,
      placementBuffer: [
        {
          placementName,
          imageBuffer
        }
      ]
    }
    return axios({
      url: requestUrl,
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      data: JSON.stringify(data)
    });
  },
  [types.GET_CROPPED_UGC_PLACEMENT]({ rootState }, { model, size, componentName, placementName, croppedPlacement }) {
    const requestUrl = this.state.endPoints.ugcentrypoint.ugcPlacementImageCrop;
    const domain =  this.state.utils.configuration.domain || window.location.origin;
    croppedPlacement.original_image = `${!croppedPlacement.original_image.includes(domain) ? domain : '' }${croppedPlacement.original_image}`;
    let data = {
      ...croppedPlacement
    };
    if(model && componentName && size){
      data.productName = model;
      data.componentName = componentName;
      data.size = size;
    }

    return axios({
      url: requestUrl,
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      data: JSON.stringify(data)
    });
  },
  [types.GENERATE_UGC_SWATCH]({ rootState, state, dispatch }, { size = '9.0 Men = 10.5 Women', imageBuffer, ugcComponent, croppedPlacement }) {
    const { pdm, selectedStyle, uploadedImageId, uploadedImageUrl } = state;
    const placements = [];
    let ugcSwatch;
    const pdmPlacements = pdm[selectedStyle].components[ugcComponent].ugc.placements;

    let dispatchType = types.GET_UGC_PLACEMENT;
    let dispatchParams = {
      model: selectedStyle,
      size,
      componentName: ugcComponent,
      placementName: pdmPlacements[0].display_name,
      imageBuffer
    };
    if ((rootState.utils.configuration.enableCropPlacement || rootState.utils.configuration.enableUseSquareImage) && croppedPlacement) {
      dispatchType = types.GET_CROPPED_UGC_PLACEMENT;
      dispatchParams = {
        model: selectedStyle,
        size,
        componentName: ugcComponent,
        placementName: pdmPlacements[0].display_name,
        croppedPlacement
      };
    }

    return new Promise((resolve, reject) => {
      dispatch(dispatchType, dispatchParams).then((data) => {
        pdmPlacements.forEach((item, idx) => {
          let placementData = { ...data.data[0] };
          if (idx == 1) {
            delete placementData.tonal_stitch_color;
          }
          placements.push(placementData);
        });
        return dispatch('ugc/GET_UGC_SWATCH', {
            productName: selectedStyle,
            size,
            componentName: ugcComponent,
            placements,
            endpointsType: 'ugcentrypoint'
          } , { root: true })
        }).then(({ data }) => {
          ugcSwatch = data;
          const ugcTemp = {
            original_image: {
              image_id: uploadedImageId,
              image_url: uploadedImageUrl
            },
            placements,
            size,
            tonal_stitch_color: placements[0].tonal_stitch_color,
            ugc_swatch: ugcSwatch
          };
          resolve(ugcTemp);
        }).catch(reject);
      }
    );
  },
  [types.GET_PRODUCT_IMAGES_BY_RECIPE_ID]({ rootState }, { images, recipeID, locale }) {
    const requestParams = {};
    if (images) requestParams.images = images; // optionally added

    const requestUrl = `${rootState.endPoints.ugcentrypoint.productImages}/${ recipeID }?${returnUrlParams(requestParams)}`;

    return http.get(requestUrl).then(({ data }) => data);
  },
  /**
   * @function GET_COLOR_MAPPING_DATA
   * Gets the color mapping data
   * @param {Array} models
   * @param {string} imageId
   */
  [types.GET_COLOR_MAPPING_DATA]({ commit, rootState }, { imageId, models }) {
    const url = `${rootState.endPoints.ugcentrypoint.getColorMapping}/${imageId}?models=${models}`;

    return http.get(url).then(({ data }) => {
      commit(types.SET_COLOR_MAPPING_DATA, data);
    });
  },
};

export default actions;
